import React, { useEffect, useState } from 'react';
import NavBar from './NavBar';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Link } from 'react-router-dom';
import './ApiKeysHelp.css';

function ApiKeysHelp() {
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="api-keys-help">
      <NavBar />

      {/* Hero Section */}
      <section className="api-keys-hero">
        <div className="api-keys-hero-content">
          <h1>Getting API Keys</h1>
          <p className="api-keys-subtitle">You need at least one API key. Pick a provider below.</p>
        </div>
      </section>

      {/* Main Content */}
      <div className="api-keys-container">

        {/* OpenAI Section */}
        <section className="provider-section">
          <h2 onClick={() => toggleSection('openai')} className="provider-header">
            <span>OpenAI (GPT)</span>
            <span className="toggle-icon">{expandedSection === 'openai' ? '−' : '+'}</span>
          </h2>
          {expandedSection === 'openai' && (
            <div className="provider-content">
              <div className="steps">
                <ol>
                  <li>Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys</a></li>
                  <li>Sign in or create an account</li>
                  <li>Click "Create new secret key"</li>
                  <li>Give it a name (optional)</li>
                  <li>Copy your API key (starts with <code>sk-</code>)</li>
                </ol>
              </div>

              <div className="env-example">
                <h4>Add to .env:</h4>
                <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`OPENAI_API_KEY=sk-your-key-here`}
                </SyntaxHighlighter>
              </div>

              <div className="code-example">
                <h4>Test it:</h4>
                <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import GPT

agent = Agent(llm=GPT())
response = agent.run("Hello!")
print(response.text)`}
                </SyntaxHighlighter>
              </div>
            </div>
          )}
        </section>

        {/* Anthropic Section */}
        <section className="provider-section">
          <h2 onClick={() => toggleSection('anthropic')} className="provider-header">
            <span>Anthropic (Claude)</span>
            <span className="toggle-icon">{expandedSection === 'anthropic' ? '−' : '+'}</span>
          </h2>
          {expandedSection === 'anthropic' && (
            <div className="provider-content">
              <div className="steps">
                <ol>
                  <li>Go to <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer">console.anthropic.com</a></li>
                  <li>Sign in or create an account</li>
                  <li><strong>Fund your account with at least $5</strong> (required to use the API)</li>
                  <li>Navigate to "API Keys" in the left sidebar</li>
                  <li>Click "Create Key"</li>
                  <li>Copy your API key (starts with <code>sk-ant-</code>)</li>
                </ol>
              </div>

              <div className="env-example">
                <h4>Add to .env:</h4>
                <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`ANTHROPIC_API_KEY=sk-ant-your-key-here`}
                </SyntaxHighlighter>
              </div>

              <div className="code-example">
                <h4>Test it:</h4>
                <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import Claude

agent = Agent(llm=Claude())
response = agent.run("Hello!")
print(response.text)`}
                </SyntaxHighlighter>
              </div>
            </div>
          )}
        </section>

        {/* Groq Section */}
        <section className="provider-section">
          <h2 onClick={() => toggleSection('groq')} className="provider-header">
            <span>Groq (Fast Inference)</span>
            <span className="toggle-icon">{expandedSection === 'groq' ? '−' : '+'}</span>
          </h2>
          {expandedSection === 'groq' && (
            <div className="provider-content">
              <div className="steps">
                <ol>
                  <li>Go to <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer">console.groq.com/keys</a></li>
                  <li>Sign in or create an account</li>
                  <li>Click "Create API Key"</li>
                  <li>Give it a name (optional)</li>
                  <li>Copy your API key (starts with <code>gsk_</code>)</li>
                </ol>
              </div>

              <div className="env-example">
                <h4>Add to .env:</h4>
                <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`GROQ_API_KEY=gsk_your-key-here`}
                </SyntaxHighlighter>
              </div>

              <div className="code-example">
                <h4>Test it:</h4>
                <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import Groq

agent = Agent(llm=Groq())
response = agent.run("Hello!")
print(response.text)`}
                </SyntaxHighlighter>
              </div>
            </div>
          )}
        </section>

        {/* Google Section */}
        <section className="provider-section">
          <h2 onClick={() => toggleSection('google')} className="provider-header">
            <span>Google (Gemini)</span>
            <span className="toggle-icon">{expandedSection === 'google' ? '−' : '+'}</span>
          </h2>
          {expandedSection === 'google' && (
            <div className="provider-content">
              <div className="steps">
                <ol>
                  <li>Go to <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com/app/apikey</a></li>
                  <li>Sign in with your Google account</li>
                  <li>Click "Create API key"</li>
                  <li>Select an existing project or create a new one</li>
                  <li>Copy your API key</li>
                </ol>
              </div>

              <div className="env-example">
                <h4>Add to .env:</h4>
                <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`GOOGLE_API_KEY=your-google-key-here`}
                </SyntaxHighlighter>
              </div>

              <div className="code-example">
                <h4>Test it:</h4>
                <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import Gemini

agent = Agent(llm=Gemini())
response = agent.run("Hello!")
print(response.text)`}
                </SyntaxHighlighter>
              </div>
            </div>
          )}
        </section>

        {/* Ollama Section */}
        <section className="provider-section">
          <h2 onClick={() => toggleSection('ollama')} className="provider-header">
            <span>Ollama (Local)</span>
            <span className="toggle-icon">{expandedSection === 'ollama' ? '−' : '+'}</span>
          </h2>
          {expandedSection === 'ollama' && (
            <div className="provider-content">
              <div className="steps">
                <ol>
                  <li>Go to <a href="https://ollama.com/download" target="_blank" rel="noopener noreferrer">ollama.com/download</a></li>
                  <li>Download and install Ollama for your OS</li>
                  <li>Open terminal and run <code>ollama pull llama3.2</code></li>
                  <li>Start the server with <code>ollama serve</code></li>
                  <li>No API key needed—runs locally on your machine!</li>
                </ol>
              </div>

              <div className="env-example">
                <h4>Add to .env (optional):</h4>
                <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`# No API key needed for Ollama
# Optionally set custom host:
# OLLAMA_HOST=http://localhost:11434`}
                </SyntaxHighlighter>
              </div>

              <div className="code-example">
                <h4>Test it:</h4>
                <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import Llama

agent = Agent(llm=Llama())
response = agent.run("Hello!")
print(response.text)`}
                </SyntaxHighlighter>
              </div>
            </div>
          )}
        </section>

        {/* Complete .env Example */}
        <section className="complete-env-section">
          <h2>Complete .env Example</h2>
          <p className="env-description">Here's what your <code>.env</code> file should look like with all providers configured:</p>
          <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`# Orchestral AI - API Keys Configuration

# OpenAI (GPT) API Key
OPENAI_API_KEY=sk-your-key-here

# Anthropic (Claude) API Key
ANTHROPIC_API_KEY=sk-ant-your-key-here

# Groq API Key
GROQ_API_KEY=gsk_your-key-here

# Google (Gemini) API Key
GOOGLE_API_KEY=your-google-key-here

# Note: You only need the keys for providers you plan to use`}
          </SyntaxHighlighter>
        </section>

        {/* Troubleshooting Section */}
        <section className="troubleshooting-section">
          <h2>Troubleshooting</h2>
          <p className="troubleshooting-text">
            <strong>If you get authentication errors:</strong> Double-check that your API key is correctly set in the <code>.env</code> file with no extra spaces or quotes. Make sure the <code>.env</code> file is in your project root directory.
          </p>
          <p className="troubleshooting-text">
            <strong>Need more help?</strong> Check out the <Link to="/">quickstart guide</Link> or visit our documentation.
          </p>
        </section>
      </div>
    </div>
  );
}

export default ApiKeysHelp;
